import { useEffect, useRef, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Alert, Stack } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { ory } from 'src/providers/authProvider';
import { Logger } from 'src/utils/Logger';
import { usePublicTenantConfig } from 'src/auth/hooks/usePublicTenantConfig';
import { useInviteSettingsFlow } from 'src/auth/hooks/useInviteSettingsFlow';
import { useAuthMethodCheck } from 'src/auth/hooks/useAuthMethodCheck';
import { WelcomeSection } from 'src/auth/invite/WelcomeSection';
import { InviteRegistrationForm } from 'src/auth/invite/InviteRegistrationForm';
import { AuthPageLayout } from 'src/auth/components/AuthPageLayout';

type InviteVariant = 'password' | 'sso_only';

export const InvitePageRoute = () => {
  const [searchParams] = useSearchParams();
  const flowId = searchParams.get('flow');
  const authState = useAuthMethodCheck(flowId);
  const creatingFlowRef = useRef(false);

  useEffect(() => {
    if (authState === 'no_flow' && !creatingFlowRef.current) {
      creatingFlowRef.current = true;
      ory
        .createBrowserSettingsFlow()
        .then(({ data }) => {
          window.location.replace(`/invite?flow=${data.id}`);
        })
        .catch(error => {
          Logger.error('Failed to create settings flow for invite page', error);
          window.location.replace('/login');
        });
    }
  }, [authState]);

  if (authState === 'loading' || authState === 'no_flow') {
    return <AuthPageLayout isLoading />;
  }

  if (authState === 'existing_user') {
    return <Navigate to="/login" replace />;
  }

  return <InvitePage flowId={flowId!} />;
};

interface InvitePageProps {
  flowId: string;
}

const InvitePage = ({ flowId }: InvitePageProps) => {
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    isLoading: isFlowLoading,
    error,
    userEmail,
    firstName,
    inviterName,
    tenantName,
    oidcNodes,
    csrfToken,
    formAction,
    updatePassword,
  } = useInviteSettingsFlow(flowId);

  const {
    config,
    isLoading: isConfigLoading,
    isError: isConfigError,
  } = usePublicTenantConfig(tenantName || undefined);

  const isLoading = isFlowLoading || isConfigLoading;
  const variant: InviteVariant =
    !isConfigError && config?.authMethod ? config.authMethod : 'password';

  const handleCreateAccount = async (password: string) => {
    setIsSubmitting(true);
    const { success } = await updatePassword(password);
    setIsSubmitting(false);

    if (success) {
      try {
        const { data: logoutFlow } = await ory.createBrowserLogoutFlow();
        await ory.updateLogoutFlow({ token: logoutFlow.logout_token });
      } catch (logoutError) {
        Logger.error(
          'Logout failed after credential setup, redirecting to login anyway',
          logoutError,
        );
      }
      window.location.replace('/login');
    }
  };

  return (
    <AuthPageLayout isLoading={isLoading} withHeader>
      {isConfigError ? (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light" w={450}>
          Something went wrong. Please try refreshing the page.
        </Alert>
      ) : (
        <Stack align="center" gap="xl">
          {!showForm && (
            <WelcomeSection
              firstName={firstName}
              inviterName={inviterName}
              userEmail={userEmail}
              error={error}
              onAcceptInvite={() => setShowForm(true)}
              variant={variant}
              oidcNodes={oidcNodes}
              formAction={formAction}
              csrfToken={csrfToken}
            />
          )}

          {showForm && variant === 'password' && (
            <InviteRegistrationForm
              userEmail={userEmail}
              error={error}
              isSubmitting={isSubmitting}
              onSubmit={handleCreateAccount}
              oidcNodes={oidcNodes}
              formAction={formAction}
              csrfToken={csrfToken}
            />
          )}
        </Stack>
      )}
    </AuthPageLayout>
  );
};
